import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Lock, LoaderCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { adminLogin, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/admin/AuthContext";

export default function AdminLoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) { setError("Please enter your email and password."); return; }
    setBusy(true);
    try {
      const res = await adminLogin(form);
      login(res.access_token, res.user);
      toast.success("Welcome back", { description: res.user.name || res.user.email });
      navigate(location.state?.from?.pathname || "/admin", { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, "Sign in failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden hero-bg noise" data-testid="admin-login-page">
      <Seo title="Admin sign in" />
      <div className="absolute inset-0 grid-lines" aria-hidden="true" />
      <div className="container-x relative grid min-h-screen items-center gap-12 py-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <a href="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="admin-login-back-link"><ArrowLeft className="h-4 w-4" /> Back to website</a>
          <img src="/assets/logo.png" alt="Jonaren Technology" className="mt-10 h-12 w-auto" />
          <h1 className="mt-8 font-heading text-4xl font-semibold tracking-tight">Content management</h1>
          <p className="lead mt-4 max-w-md">Manage inquiries, services, products, testimonials, FAQs and site settings for jntechno.com.</p>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <form onSubmit={onSubmit} className="card-solid p-8" data-testid="admin-login-form">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Lock className="h-5 w-5" /></span>
            <h2 className="mt-5 font-heading text-2xl font-semibold">Sign in</h2>
            <p className="mt-1 text-sm text-muted-foreground">Authorised administrators only.</p>
            <div className="mt-6 space-y-5">
              <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl bg-white" data-testid="admin-login-email-input" /></div>
              <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl bg-white" data-testid="admin-login-password-input" /></div>
              {error && <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert" data-testid="admin-login-error">{error}</p>}
              <Button type="submit" disabled={busy} className="h-11 w-full rounded-xl" data-testid="admin-login-submit-button">{busy && <LoaderCircle className="h-4 w-4 animate-spin" />} {busy ? "Signing in..." : "Sign in"}</Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
